import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useLang } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "goldenelia_chat_session";

const TEXTS = {
  el: {
    title: "Elia · AI Σύμβουλος",
    sub: "Ρώτησέ με για δώρα, προϊόντα, αποστολή",
    placeholder: "Γράψε ένα μήνυμα…",
    send: "Αποστολή",
    welcome:
      "Καλώς ήρθες στο Golden Elia! Είμαι η Elia. Πες μου τι ψάχνεις και θα σε βοηθήσω να βρεις το τέλειο δώρο 🌿",
    error: "Συγγνώμη, κάτι πήγε στραβά. Δοκίμασε ξανά.",
    button: "Ρώτησε την Elia",
  },
  en: {
    title: "Elia · AI Concierge",
    sub: "Ask me about gifts, products, shipping",
    placeholder: "Type a message…",
    send: "Send",
    welcome:
      "Welcome to Golden Elia! I'm Elia. Tell me what you're looking for and I'll help you find the perfect gift 🌿",
    error: "Sorry, something went wrong. Try again.",
    button: "Ask Elia",
  },
  bg: {
    title: "Elia · AI Консиерж",
    sub: "Попитайте за подаръци, продукти, доставка",
    placeholder: "Напишете съобщение…",
    send: "Изпрати",
    welcome: "Добре дошли в Golden Elia! Аз съм Elia. Кажете ми какво търсите 🌿",
    error: "Извинете, нещо се обърка. Опитайте отново.",
    button: "Питайте Elia",
  },
  ru: {
    title: "Elia · AI Консьерж",
    sub: "Спросите о подарках, продуктах, доставке",
    placeholder: "Напишите сообщение…",
    send: "Отправить",
    welcome: "Добро пожаловать в Golden Elia! Я Elia. Скажите, что вы ищете 🌿",
    error: "Извините, что-то пошло не так. Попробуйте снова.",
    button: "Спросить Elia",
  },
};

const newSessionId = () => `goldenelia-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const AIChat = () => {
  const { lang } = useLang();
  const t = TEXTS[lang] || TEXTS.en;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const saved = localStorage.getItem(SESSION_KEY);
    if (saved) return saved;
    const id = newSessionId();
    localStorage.setItem(SESSION_KEY, id);
    return id;
  });
  const scrollRef = useRef(null);

  // Seed welcome on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: t.welcome }]);
    }
  }, [open, messages.length, t.welcome]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((arr) => [...arr, { role: "user", text: msg }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: msg,
        lang,
      });
      setMessages((arr) => [...arr, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((arr) => [...arr, { role: "assistant", text: t.error }]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    const id = newSessionId();
    localStorage.setItem(SESSION_KEY, id);
    setSessionId(id);
    setMessages([{ role: "assistant", text: t.welcome }]);
  };

  return (
    <>
      {/* Toggle button */}
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-24 right-5 z-[130] w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
        style={{
          background: "linear-gradient(145deg, #d6b56b 0%, #8c6f3b 100%)",
          boxShadow: "0 6px 24px -6px rgba(201,169,97,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
        }}
        aria-label={t.button}
      >
        {open ? (
          <X size={22} color="#0a0a0a" strokeWidth={2.2} />
        ) : (
          <MessageCircle size={22} color="#0a0a0a" strokeWidth={2.2} />
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-44 right-4 left-4 sm:right-5 sm:left-auto z-[130] sm:w-[400px] max-h-[calc(100vh-220px)] flex flex-col border border-[color:var(--goldenelia-gold)]/40"
          style={{
            background: "rgba(8,8,8,0.96)",
            backdropFilter: "blur(20px) saturate(140%)",
            WebkitBackdropFilter: "blur(20px) saturate(140%)",
            boxShadow: "0 30px 80px -16px rgba(0,0,0,0.85)",
          }}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-[color:var(--goldenelia-gold)]/25 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-[color:var(--goldenelia-gold)] to-[#8c6f3b]">
              <Sparkles size={16} color="#0a0a0a" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display gold-gradient-text text-[13px] tracking-[0.2em]">
                {t.title}
              </p>
              <p className="font-body text-[10px] text-[color:var(--goldenelia-cream)]/60 truncate">
                {t.sub}
              </p>
            </div>
            <button
              onClick={reset}
              data-testid="ai-chat-reset"
              className="font-display text-[9px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-cream)]/50 hover:text-[color:var(--goldenelia-gold)] transition-colors"
              aria-label="reset"
            >
              Νέα
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="ai-chat-messages"
            className="flex-1 overflow-y-auto px-4 py-5 space-y-3 min-h-[280px]"
            style={{ scrollbarWidth: "thin" }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`ai-chat-msg-${m.role}-${i}`}
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-[13.5px] leading-relaxed font-body whitespace-pre-wrap ${
                    m.role === "user"
                      ? "rounded-br-sm"
                      : "rounded-bl-sm"
                  }`}
                  style={
                    m.role === "user"
                      ? {
                          background: "linear-gradient(145deg, #e6c98a 0%, #c9a961 100%)",
                          color: "#0a0a0a",
                          fontWeight: 500,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        }
                      : {
                          background: "rgba(245, 232, 199, 0.95)",
                          color: "#1a1a1a",
                          fontWeight: 400,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                        }
                  }
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-4 py-3 bg-[color:var(--goldenelia-cream)]/10 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--goldenelia-gold)] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--goldenelia-gold)] animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--goldenelia-gold)] animate-bounce" style={{ animationDelay: "240ms" }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-[color:var(--goldenelia-gold)]/20 p-3 flex gap-2">
            <input
              data-testid="ai-chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.placeholder}
              maxLength={2000}
              className="flex-1 bg-[color:var(--goldenelia-cream)]/5 border border-[color:var(--goldenelia-gold)]/40 px-4 py-2.5 text-[13.5px] font-body focus:outline-none focus:border-[color:var(--goldenelia-gold)] focus:bg-[color:var(--goldenelia-cream)]/10 transition-colors"
              style={{ color: "#f5e8c7" }}
            />
            <button
              data-testid="ai-chat-send"
              type="submit"
              disabled={!input.trim() || sending}
              className="px-4 py-2.5 border border-[color:var(--goldenelia-gold)]/60 text-[color:var(--goldenelia-gold)] hover:bg-[color:var(--goldenelia-gold)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label={t.send}
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AIChat;
